// services/videoService.js
import axios from 'axios';

const apiUrl = process.env.REACT_APP_PAYMENT_LINK_URL;

/**
 * Fetches the onboarding video series for a lead.
 *
 * The backend decides eligibility (allotment Payment Complete + KYC filled +
 * a CRM status that has videos tagged to it) and returns the videos in
 * sequence, each with any saved watch progress.
 *
 * Returns null when nothing should be shown — which is the normal outcome for
 * most customers, not an error.
 *
 * Shape on success:
 *   { videos: [{ id, sequence, title, videoUrl, progress }], resumeSequence }
 */
export const getCustomerVideo = async (leadId) => {
  if (!leadId) return null;

  try {
    const response = await axios.get(`${apiUrl}customer-video`, {
      params: { leadId },
    });

    const data = response.data && response.data.data;
    if (!response.data || !response.data.success || !data) return null;
    if (!data.eligible || !data.videos || !data.videos.length) return null;

    // Returned even when shouldShow is false: a customer who clicked Next
    // through everything is "done" (so the series does not auto-open) but may
    // still have watched none of it, which is what the reminder is for.
    return {
      videos: data.videos,
      shouldShow: data.shouldShow,
      resumeSequence: data.resumeSequence,
      reminderSequence: data.reminderSequence,
      pendingCount: data.pendingCount,
    };
  } catch (error) {
    // Never let this block the dashboard: if the lookup fails the customer just
    // does not get the videos.
    console.error('Error fetching customer videos:', error);
    return null;
  }
};

/**
 * Records watch progress. One endpoint covers every moment worth recording:
 * playback starting, the periodic ping while watching, and finishing a video —
 * the backend merges each call into a single row per lead+video.
 *
 * @param {object} p
 * @param {string} p.leadId
 * @param {string} p.videoId
 * @param {number} p.position        current time, seconds
 * @param {number} [p.videoDuration] total length, seconds
 * @param {number} [p.delta]         seconds actually watched since the last call
 * @param {string} [p.status]        'started' | 'completed'
 */
export const saveVideoProgress = async ({
  leadId,
  videoId,
  position,
  videoDuration,
  delta,
  status,
}) => {
  if (!leadId || !videoId) return null;

  try {
    const response = await axios.post(`${apiUrl}customer-video/progress`, {
      leadId,
      videoId,
      position,
      videoDuration,
      delta,
      status,
    });
    return (response.data && response.data.data) || null;
  } catch (error) {
    // Tracking is not worth interrupting playback for.
    console.error('Error saving video progress:', error);
    return null;
  }
};
